import { useState } from 'react';
import { stopRobots, useMapRecord, useMaps } from '../api/queries';
import { useT } from '../i18n';
import { useLive } from '../live/store';
import { stopAutonomy } from '../lib/autonomy';
import { lockWheels } from '../lib/handPush';
import { MODE_TONE, useRobotMode } from '../lib/mode';
import { toastErr } from './toast';


/**
 * The robot's current mode as one chip (Fleet tile + Robot header), with the exit action next to it:
 * hand push → lock wheels; autonomy → stop autonomy; navigation / tour / stuck / going to charge → Stop.
 */
export function ModeChip({ serial, compact }: { serial: string; compact?: boolean }) {
  const t = useT();
  const mode = useRobotMode(serial);
  const mapName = useLive((s) => s.robots[serial]?.status?.currentMap);
  const maps = useMaps();
  const mapId = mode.kind === 'navigation' && mode.pointId ? maps.data?.find((m) => m.name === mapName)?.id : undefined;
  const rec = useMapRecord(mapId);
  const [busy, setBusy] = useState(false);
  if (mode.kind === 'offline') return null;
  const pointName = mode.kind === 'navigation' && mode.pointId ? (rec.data?.points.find((p) => p.id === mode.pointId)?.name ?? mode.pointId) : null;
  const label = (() => {
    switch (mode.kind) {
      case 'estop':
        return `⛔ ${t('mode.estop')}`;
      case 'handPush':
        return `✋ ${t('mode.handPush')}`;
      case 'stuck':
        return `⚠ ${mode.failed ? t('mode.stuckFailed') : t('mode.stuck')}`;
      case 'charging':
        return `⚡ ${t('mode.charging')}`;
      case 'goingCharge':
        return `⚡ ${t('mode.goingCharge')}`;
      case 'chargeFailed':
        return `⚡ ${t('mode.chargeFailed')}`;
      case 'autonomy':
        return `${t('mode.autonomy')}${mode.state ? ` · ${['listening', 'thinking', 'speaking', 'moving', 'paused', 'idle'].includes(mode.state) ? t(`auto.state.${mode.state}` as 'auto.state.idle') : mode.state}` : ''}`;
      case 'tour':
        return `${t('mode.tour')}${mode.name ? ` “${mode.name}”` : ''}`;
      case 'navigation':
        return pointName ? t('mode.navTo', { point: pointName }) : t('mode.navigation');
      case 'task':
        return t('mode.task', { task: mode.task });
      default:
        return t('mode.normal');
    }
  })();
  // the part that must stay visible when the chip is narrow (tour step)
  const extra = mode.kind === 'tour' && mode.step !== null && mode.total !== null ? `${mode.step}/${mode.total}` : null;
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      toastErr(e);
    } finally {
      setBusy(false);
    }
  };
  const exit: { label: string; title: string; danger?: boolean; fn: () => Promise<unknown> } | null =
    mode.kind === 'handPush'
      ? { label: `🔒 ${compact ? t('mode.lockShort') : t('hand.lock')}`, title: t('mode.exitHandPush'), fn: () => lockWheels(serial) }
      : mode.kind === 'autonomy'
        ? { label: compact ? t('mode.stopShort') : t('auto.stop'), title: t('auto.stop'), danger: true, fn: () => stopAutonomy(serial) }
        : mode.kind === 'navigation' || mode.kind === 'tour' || mode.kind === 'stuck' || mode.kind === 'goingCharge' || mode.kind === 'task'
          ? { label: t('mode.stopShort'), title: t('mode.stopTitle'), danger: true, fn: () => stopRobots([serial]) }
          : null;
  return (
    <span className={`mode-wrap${compact ? ' compact' : ''}`}>
      <span className={`mode-chip tone-${MODE_TONE[mode.kind]}${mode.kind === 'estop' ? ' blink' : ''}`} role="status" title={extra ? `${label} · ${extra}` : label}>
        <span className="mode-label">{label}</span>
        {extra && <span className="mode-extra">· {extra}</span>}
      </span>
      {mode.kind === 'estop' && !compact && <span className="mode-hint">{t('mode.estopHint')}</span>}
      {exit && (
        <button type="button" className={`btn xs mode-exit${exit.danger ? ' danger-outline' : ''}`} disabled={busy} title={exit.title} onClick={() => void run(exit.fn)}>
          {exit.label}
        </button>
      )}
    </span>
  );
}
